<section class="relative flex flex-col h-full sm:h-[31.8125rem]">
  <p-button
    icon="true"
    class="absolute top-[0.75rem] right-[0.75rem]"
    severity="contrast"
    rounded="true"
    styleClass="!size-[1.75rem] !px-0 !py-0"
    [text]="true"
    (click)="cancel()">
    <svg-icon src="icons/icon-close-popup.svg" name="icon-close-popup" svgClass="!text-(--p-primary-200)"></svg-icon>
  </p-button>

  <div class="flex flex-col gap-4 px-5 pt-5 pb-5 flex-1 min-h-0 overflow-y-auto">
    <h3
      class="text-[1rem] font-semibold text-(--p-primary-500) pr-8">
      {{ '@@ld-create-logger-noise-report-dialog__title' | translate }}
    </h3>
    <div class="flex items-center gap-2">
      <p-button
        [label]="'@@shared-loggers__label' | translate"
        [severity]="activeTab() === LoggerNoiseReportTab.Loggers ? 'primary' : 'secondary'"
        styleClass="h-[2rem] !px-[0.9375rem]"
        (onClick)="setActiveTab(LoggerNoiseReportTab.Loggers)" />

      <p-button
        [label]="'@@ld-create-correlation-report-dialog__parameters-tab' | translate"
        [severity]="activeTab() === LoggerNoiseReportTab.Parameters ? 'primary' : 'secondary'"
        styleClass="h-[2rem] !px-[0.9375rem]"
        (onClick)="setActiveTab(LoggerNoiseReportTab.Parameters)" />
    </div>

    @if (activeTab() === LoggerNoiseReportTab.Loggers) {
      <p-table
        [dt]="gridLinesDatatableToken"
        [value]="sortedLoggers()"
        dataKey="id"
        class="text-xs"
        tableStyleClass="table-fixed w-full"
        showGridlines
        stripedRows
        [scrollable]="true"
        scrollHeight="16rem">
        <ng-template #sortableHeaderContent let-field="field" let-labelKey="labelKey">
          <div class="inline-flex items-center gap-1">
            <span [translate]="labelKey"></span>
            @if (sortField() === field) {
              <svg-icon
                src="icons/icon-arrow-long-down.svg"
                name="icon-arrow-long-down"
                [svgClass]="sortOrder() === SortOrder.Asc ? 'rotate-180' : ''"
                class="text-(--p-primary-500)"></svg-icon>
            }
          </div>
        </ng-template>

        <ng-template #header>
          <tr class="text-[0.625rem] leading-[150%]">
            <th class="!w-[4rem] !p-2">
              <p-checkbox
                class="flex"
                [binary]="true"
                [indeterminate]="partiallySelected()"
                [ngModel]="allSelected()"
                (click)="$event.preventDefault(); toggleAll()">
                <ng-template #checkboxicon>
                  @if (allSelected()) {
                    <svg-icon
                      src="icons/icon-checkmark-correct.svg"
                      name="icon-checkmark-correct"
                      class="text-(--p-surface-0) size-[0.5rem] shrink-0"></svg-icon>
                  } @else if (partiallySelected()) {
                    <svg-icon
                      src="icons/icon-checkmark-partial.svg"
                      name="icon-checkmark-partial"
                      class="text-(--p-primary-500) size-[0.375rem] shrink-0"></svg-icon>
                  }
                </ng-template>
              </p-checkbox>
            </th>

            <th class="cursor-pointer select-none" (click)="toggleSort('predictionScore')">
              <ng-container
                [ngTemplateOutlet]="sortableHeaderContent"
                [ngTemplateOutletContext]="{
                  field: 'predictionScore',
                  labelKey: '@@shared-leak-score__text',
                }"></ng-container>
            </th>

            <th class="cursor-pointer select-none" (click)="toggleSort('logger')">
              <ng-container
                [ngTemplateOutlet]="sortableHeaderContent"
                [ngTemplateOutletContext]="{ field: 'logger', labelKey: '@@shared-logger__text' }"></ng-container>
            </th>
          </tr>
        </ng-template>

        <ng-template #body let-row>
          <tr class="h-[2.5625rem]">
            <td class="!p-2">
              <div class="flex items-center gap-[0.375rem]">
                <p-checkbox
                  class="flex"
                  [binary]="true"
                  [ngModel]="!!selectedIds()[row.id]"
                  (click)="$event.preventDefault(); toggleRow(row.id)">
                  <ng-template #checkboxicon>
                    @if (selectedIds()[row.id]) {
                      <svg-icon
                        src="icons/icon-checkmark-correct.svg"
                        name="icon-checkmark-correct"
                        class="text-(--p-surface-0) size-[0.5rem] shrink-0"></svg-icon>
                    }
                  </ng-template>
                </p-checkbox>

                @let icon = loggerIcon(row);
                <svg-icon
                  [src]="icon.src"
                  [name]="icon.name ?? icon.src"
                  class="flex original-icon shrink-0"
                  svgClass="!size-[1.125rem]"></svg-icon>
              </div>
            </td>

            <td>
              <div class="flex items-center gap-2 overflow-hidden">
                <gc-ld-logger-noise-leak-score-gauge
                  [status]="row.predictionStatus"
                  [width]="28"></gc-ld-logger-noise-leak-score-gauge>

                @if (row.predictionScore === undefined) {
                  <svg-icon
                    src="icons/icon-alarm-outline.svg"
                    name="icon-alarm-outline"
                    class="size-5 block [&_svg]:stroke-[1.2px] text-(--p-orange-800)"></svg-icon>
                } @else {
                  <div
                    class="min-w-[1.5rem] text-[0.6875rem] font-medium leading-[150%]"
                    translate="@@shared-percentage"
                    [translateParams]="{ I18N_VAR_VALUE: row.predictionScore }"></div>
                }
              </div>
            </td>

            <td class="truncate">{{ formattingData.formatSerialNo(row.serialNo) }}</td>
          </tr>
        </ng-template>
      </p-table>
    }

    @if (activeTab() === LoggerNoiseReportTab.Parameters) {
      <form [formGroup]="parametersForm" class="flex flex-col gap-4">
        <div class="flex flex-col gap-3">
          @for (checkbox of parametersCheckboxOptions; track checkbox.controlKey) {
            <div class="flex items-center gap-3">
              <p-checkbox
                class="flex"
                [binary]="true"
                [formControlName]="checkbox.controlKey"
                [inputId]="checkbox.controlKey">
                <ng-template #checkboxicon>
                  @if (parametersForm.controls[checkbox.controlKey].value) {
                    <svg-icon
                      src="icons/icon-checkmark-correct.svg"
                      name="icon-checkmark-correct"
                      class="text-(--p-surface-0) size-[0.5rem] shrink-0"></svg-icon>
                  }
                </ng-template>
              </p-checkbox>
              <label
                [for]="checkbox.controlKey"
                class="text-[0.75rem] text-(--p-gray-950) font-semibold cursor-pointer select-none"
                [translate]="checkbox.labelKey"></label>
            </div>
          }
        </div>

        <p-divider styleClass="!my-0 before:!border-(--p-primary-50)"></p-divider>

        <div class="flex flex-col gap-2">
          <label
            for="ldLoggerNoiseReportCoordinates"
            class="text-[0.75rem] text-(--p-primary-300) font-semibold leading-[150%]"
            translate="@@shared-coordinates__text"></label>

          <p-select
            #coordinatesSelect
            inputId="ldLoggerNoiseReportCoordinates"
            formControlName="coordinates"
            [options]="coordinatesOptions"
            styleClass="!bg-white !rounded-[0.5rem] !h-[2.75rem] !items-center !border !border-(--p-primary-50) [&.p-focus]:!border-2 [&.p-focus]:!border-(--p-sky-300) [&.p-focus]:!outline-none [&.p-select-open]:!shadow-[0px_1px_8px_0px_var(--p-other-opacity-sky-20)] [&_.p-select-label]:!text-(--p-gray-950) [&_.p-select-dropdown]:!pr-3 [&_.p-select-dropdown]:!pl-0 w-full"
            panelStyleClass="[&_.p-select-option]:!h-9 [&_.p-select-option]:!flex [&_.p-select-option]:!items-center [&_.p-select-option]:!justify-start [&_.p-select-option]:!pl-3 [&_.p-select-option]:!pr-1 [&_.p-select-option]:!py-0 [&_.p-select-option]:!rounded-[0.25rem] [&_.p-select-option]:!text-[0.6875rem] [&_.p-select-option]:!text-(--p-gray-950) [&_.p-select-option.p-focus]:!bg-(--p-neutral-500) [&_.p-select-option.p-select-option-selected]:!bg-white [&_.p-select-option.p-select-option-selected.p-focus]:!bg-(--p-neutral-500)">
            <ng-template let-selectedOption #selectedItem>
              @if (selectedOption) {
                <span [translate]="coordinatesLabel(selectedOption)"></span>
              }
            </ng-template>

            <ng-template let-value #item>
              <div class="flex items-center justify-between w-full pr-1">
                <span [translate]="coordinatesLabel(value)"></span>
                @if (value === parametersForm.controls.coordinates.value) {
                  <svg-icon
                    src="icons/icon-checked.svg"
                    name="icon-checked"
                    class="original-icon !size-[1rem]"></svg-icon>
                }
              </div>
            </ng-template>

            <ng-template #dropdownicon>
              <svg-icon
                src="icons/icon-arrow-down.svg"
                name="icon-arrow-down"
                class="!text-(--p-gray-500) !size-[1.25rem]"
                [svgClass]="coordinatesSelect.overlayVisible ? 'rotate-180' : ''"></svg-icon>
            </ng-template>
          </p-select>
        </div>

        <div class="flex flex-col gap-2">
          <label
            for="ldLoggerNoiseReportAdditionalInformation"
            class="text-[0.75rem] text-(--p-primary-300) font-semibold leading-[150%]"
            translate="@@ld-create-report-dialog__additional-info-label"></label>

          <textarea
            pTextarea
            id="ldLoggerNoiseReportAdditionalInformation"
            formControlName="additionalInformation"
            [placeholder]="'@@ld-create-report-dialog__additional-info-placeholder' | translate"
            class="resize-none min-h-[5.625rem] w-full !text-[0.75rem] !font-medium !text-(--p-gray-950) !border !border-(--p-primary-50) focus:!border-2 focus:!border-(--p-sky-300) focus:!outline-none focus:!shadow-[0px_1px_8px_0px_var(--p-other-opacity-sky-20)] placeholder:!text-(--p-gray-300)"></textarea>
        </div>
      </form>
    }
  </div>

  <div class="flex items-center justify-between gap-2 h-16 px-5 bg-white border-t border-t-(--p-primary-50)">
    <span
      class="text-[0.75rem] font-medium text-(--p-gray-950)"
      translate="@@ld-create-logger-noise-report-dialog__selected-count"
      [translateParams]="{ I18N_VAR_SELECTED: selectedCount(), I18N_VAR_TOTAL: totalCount() }"></span>

    <div class="flex items-center gap-1">
      <p-button
        [label]="'@@shared-cancel__text' | translate"
        [outlined]="true"
        styleClass="h-[2.5rem] lg:h-[2rem] !px-[1.4375rem] lg:!px-[0.9375rem]"
        (onClick)="cancel()" />
      <p-button
        [label]="'@@ld-create-report-dialog__preview-btn' | translate"
        [disabled]="isPreviewDisabled()"
        styleClass="h-[2.5rem] lg:h-[2rem] !px-[1.4375rem] lg:!px-[0.9375rem]"
        (onClick)="preview()" />
    </div>
  </div>
</section>
